'use client';

import { QuestionBlock as QuestionBlockType, Answer } from './types';

interface QuestionBlockProps {
  block: QuestionBlockType;
  answers: Answer;
  onAnswer: (questionId: string, value: number) => void;
  showValidation: boolean;
}

const scaleLabels: Record<number, string> = {
  1: 'Não acontece',
  2: 'Acontece raramente',
  3: 'Existe, mas com limitações',
  4: 'Funciona de forma consistente',
  5: 'Está plenamente assegurado',
};

export default function QuestionBlock({ block, answers, onAnswer, showValidation }: QuestionBlockProps) {
  return (
    <div className="space-y-8">
      {/* Block header card */}
      <div
        className="p-6"
        style={{
          borderRadius: '5px',
          backgroundColor: '#FFFFFF',
          border: '1px solid #F3F4F6',
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        }}
      >
        <div className="flex items-center gap-3 mb-3">
          <div
            className="flex items-center justify-center w-10 h-10 text-white flex-shrink-0"
            style={{ borderRadius: '5px', backgroundColor: '#272727' }}
          >
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem', fontWeight: 500 }}>
              {String(block.blockNumber).padStart(2, '0')}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="h-px w-3" style={{ backgroundColor: '#D1D5DB' }} />
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6B7280' }}>
                Bloco {block.blockNumber} de 4
              </span>
            </div>
            <h2
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.25rem', fontWeight: 700, color: '#111111' }}
            >
              {block.title}
            </h2>
          </div>
        </div>
        <p
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', color: '#6B7280', lineHeight: 1.65, paddingLeft: '3.25rem' }}
        >
          {block.description}
        </p>
      </div>

      <div className="space-y-5">
        {block.questions.map((question, idx) => {
          const isAnswered = answers[question.id] !== undefined;
          const hasError = showValidation && !isAnswered;

          return (
            <div
              key={question.id}
              className="bg-white p-6 transition-shadow duration-200"
              style={{
                borderRadius: '5px',
                border: hasError ? '1px solid #FECACA' : isAnswered ? '1px solid #D1D5DB' : '1px solid #F3F4F6',
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
              }}
            >
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="h-px w-3" style={{ backgroundColor: '#D1D5DB' }} />
                  <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B7280' }}>
                    Questão {block.blockNumber}.{idx + 1}
                  </span>
                </div>
                <p
                  style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', color: '#111111', lineHeight: 1.65, fontWeight: 500 }}
                >
                  {question.text}
                </p>
                {hasError && (
                  <p
                    className="mt-1.5"
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.8rem', color: '#DC2626' }}
                    role="alert"
                  >
                    Por favor, selecione uma resposta para continuar.
                  </p>
                )}
              </div>

              {/* Desktop scale — 5 buttons */}
              <div className="hidden md:block">
                <div className="flex items-stretch gap-2">
                  {[1, 2, 3, 4, 5].map((val) => {
                    const selected = answers[question.id] === val;
                    return (
                      <button
                        key={val}
                        type="button"
                        onClick={() => onAnswer(question.id, val)}
                        className="flex-1 flex flex-col items-center gap-1.5 px-2 py-3 text-center transition-all duration-150 focus:outline-none"
                        style={{
                          borderRadius: '5px',
                          border: selected ? '2px solid #272727' : '1px solid #E5E7EB',
                          backgroundColor: selected ? '#272727' : '#FAFAFA',
                          color: selected ? '#FFFFFF' : '#374151',
                          minHeight: '72px',
                          cursor: 'pointer',
                        }}
                        aria-pressed={selected}
                        aria-label={`${val} — ${scaleLabels[val]}`}
                        onMouseEnter={(e) => {
                          if (!selected) {
                            (e.currentTarget as HTMLButtonElement).style.borderColor = '#9CA3AF';
                            (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#F3F4F6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!selected) {
                            (e.currentTarget as HTMLButtonElement).style.borderColor = '#E5E7EB';
                            (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#FAFAFA';
                          }
                        }}
                      >
                        <span
                          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '1.1rem', fontWeight: 600, lineHeight: 1 }}
                        >
                          {val}
                        </span>
                        <span
                          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.68rem', lineHeight: 1.3, color: selected ? 'rgba(255,255,255,0.85)' : '#9CA3AF' }}
                        >
                          {scaleLabels[val]}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-1.5 px-1">
                  <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: '#9CA3AF' }}>Não acontece</span>
                  <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: '#9CA3AF' }}>Está plenamente assegurado</span>
                </div>
              </div>

              {/* Mobile scale — vertical list */}
              <div className="md:hidden space-y-2">
                {[1, 2, 3, 4, 5].map((val) => {
                  const selected = answers[question.id] === val;
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => onAnswer(question.id, val)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-150 focus:outline-none"
                      style={{
                        borderRadius: '5px',
                        border: selected ? '2px solid #272727' : '1px solid #E5E7EB',
                        backgroundColor: selected ? '#272727' : '#FAFAFA',
                        color: selected ? '#FFFFFF' : '#374151',
                        minHeight: '48px',
                        cursor: 'pointer',
                      }}
                      aria-pressed={selected}
                    >
                      <span
                        className="flex-shrink-0 flex items-center justify-center"
                        style={{
                          width: 28, height: 28,
                          borderRadius: '5px',
                          backgroundColor: selected ? 'rgba(255,255,255,0.2)' : '#F3F4F6',
                          fontFamily: 'JetBrains Mono, monospace',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                        }}
                      >
                        {val}
                      </span>
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem' }}>{scaleLabels[val]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}